import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import Logo from './Logo';
import Navbar from './Navbar';
import AccountSettings from './AccountSettings';
import { PAGE_MAX_WIDTH, PAGE_CONTAINER_MARK } from './layout';

// Desktop breakpoint: the media queries below switch to the stacked mobile
// layout at <=600px, so aligned sizing only applies above that.
const DESKTOP_QUERY = '(min-width: 601px)';

// Logo.png (565x315) has transparent padding baked into the canvas, so the
// visible artwork does not fill the image box. These fractions (measured from
// the asset's alpha bounds) let us oversize the image box so the *artwork*
// lands on the alignment target rather than the box edge. Update if the asset
// is re-exported with different margins.
const LOGO_ART_TOP_FRAC = 9 / 315;
const LOGO_ART_BOT_FRAC = 13 / 315;
const LOGO_ART_FRAC = 1 - LOGO_ART_TOP_FRAC - LOGO_ART_BOT_FRAC;

// The logo artwork is sized a little taller than a nav button so the brand
// mark anchors the bar rather than reading as just another button.
const LOGO_BUTTON_SCALE = 1.4;

const Header = ({
  loggedInUser,
  setLoggedInUser,
  topText = "Meet",
  midText = "Vicktoria",
  botText = "A Humanlike Ethical AI Companion",
  subBotText = ""
}) => {
  // On both layouts the logo is the leftmost item in the centered nav row and
  // is sized/positioned to sit vertically centered on the button row.
  // rowRef: the header flex row. mobileRowRef: the nav row holding the logo +
  // Navbar. logoWrapRef: the wrapper around the in-row logo. A
  // `.nav-btn-responsive` button inside mobileRowRef is the measurement target.
  const rowRef = useRef(null);
  const mobileRowRef = useRef(null);
  const logoWrapRef = useRef(null);

  // { height, marginTop } in px, or null to fall back to default logo sizing.
  const [logoAlign, setLogoAlign] = useState(null);

  // Commit an align result only when it changes meaningfully, so a
  // ResizeObserver-driven recompute can't feed back into an infinite loop.
  const commit = useCallback((next) => {
    setLogoAlign((prev) => {
      if (next === null) return prev === null ? prev : null;
      if (
        prev &&
        Math.abs(prev.height - next.height) < 0.5 &&
        Math.abs(prev.marginTop - next.marginTop) < 0.5
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const recompute = useCallback(() => {
    // Measure a nav button (the Navbar lives inside mobileRowRef on both
    // layouts) and size the logo artwork a little taller than it.
    const mrow = mobileRowRef.current;
    const wrap = logoWrapRef.current;
    const btn = mrow ? mrow.querySelector('.nav-btn-responsive') : null;
    if (!mrow || !wrap || !btn) {
      commit(null);
      return;
    }

    const btnRect = btn.getBoundingClientRect();
    if (btnRect.height <= 0) return; // layout not ready yet (e.g. jsdom)

    const artHeight = btnRect.height * LOGO_BUTTON_SCALE;
    // Oversize the image box so the opaque artwork is `artHeight` tall (the PNG
    // has transparent padding baked in), then center it on the button row.
    const boxHeight = artHeight / LOGO_ART_FRAC;
    const btnCenter = btnRect.top + btnRect.height / 2;
    const boxTop = btnCenter - boxHeight / 2;
    const wrapTop = wrap.getBoundingClientRect().top;

    commit({ height: boxHeight, marginTop: boxTop - wrapTop });
  }, [commit]);

  useLayoutEffect(() => {
    recompute();

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => recompute())
        : null;
    if (ro) {
      if (rowRef.current) ro.observe(rowRef.current);
      if (mobileRowRef.current) ro.observe(mobileRowRef.current);
    }

    window.addEventListener('resize', recompute);

    const mql =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(DESKTOP_QUERY)
        : null;
    mql?.addEventListener?.('change', recompute);

    // Web fonts (Inter) can change the button height after first paint.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recompute).catch(() => {});
    }

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', recompute);
      mql?.removeEventListener?.('change', recompute);
    };
  }, [recompute]);

  return (
    <>
    <style>{`
      .ghost-element { display: none; }

      @media screen and (max-width: 600px) {
        .sticky-nav { position: relative !important; top: auto !important; }
      }

      @media screen and (min-width: 300px) and (max-width: 600px) {
        .desktop-only { display: none !important; }
        .ghost-element { display: block !important; }
        .ghost-flex { display: flex !important; }
        .header-container {
          flex-direction: column !important;
          align-items: center !important;
          padding: 10px 0 !important;
        }
        .mobile-top-row {
          display: flex !important;
          flex-direction: row !important;
          justify-content: center !important;
          align-items: flex-start !important;
          width: 100% !important;
          gap: 15px !important;
          margin-bottom: 10px !important;
        }
        .mobile-title-card {
          width: 100% !important;
          text-align: center !important;
          margin-top: 15px !important;
          padding: 0 10px !important;
          box-sizing: border-box !important;
        }
        .mobile-title-card h1 {
          font-size: clamp(40px, 12vw, 60px) !important;
          margin: 5px 0 !important;
          line-height: 1 !important;
        }
        .signed-in-banner {
          padding: 3px 8px !important;
          height: auto !important;
          justify-content: center !important;
        }
        .user-name-text {
          font-size: 14px !important;
          margin-left: 8px !important;
        }
      }
    `}</style>

    <nav className="sticky-nav" style={{ backgroundColor: '#D1DEE6', width: '100%', position: 'sticky', top: 0, zIndex: 1000, paddingBottom: '40px' }}>
      {/* Account Settings Banner */}
      {loggedInUser && (
      <div className="signed-in-banner" style={{
        position: 'absolute',
        position: 'relative',
        width: '100%',
        backgroundColor: '#4a7094ff', 
        padding: '6px 20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#FFFFFF',
        fontFamily: 'Inter, sans-serif',
        zIndex: 1000000
      }}>
        <AccountSettings 
          loggedInUser={loggedInUser} 
          setLoggedInUser={setLoggedInUser} 
          
        />

        {/* Hide "You are signed in as:" on mobile entirely to save space */}
        <span className="desktop-only" style={{ fontSize: '16px', fontWeight: '300', marginRight: '30px' }}>
          You are signed in as:
        </span>

        <span className="user-name-text" style={{ fontSize: '18px', fontWeight: '700' }}>
          {loggedInUser.name}
        </span>
      </div>
      )}

      <div className='header-container' ref={rowRef} data-layout={PAGE_CONTAINER_MARK} style={{
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        padding: '6px 5% 0 5%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', flex: 1 }}>

          {/* --- NAV ROW: logo (leftmost) + Navbar, centered as a group --- */}
          <div
            className="mobile-top-row"
            ref={mobileRowRef}
            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start', gap: '15px', width: '100%' }}
          >
             {/* alignSelf flex-start pins the wrapper's top so the computed
                 marginTop lands the logo centered on the button row without a
                 flex re-centering feedback loop. */}
             <div className="logo-slot" ref={logoWrapRef} style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                <Logo align={logoAlign}/>
             </div>
             <Navbar loggedInUser={loggedInUser} setLoggedInUser={setLoggedInUser}/>
          </div>

          {/* --- DESKTOP TITLE CARD --- */}
          <div className="title-card-text desktop-only" style={{ textAlign: 'center', marginBottom: '20px' }}>
            {topText && (
              <p style={{ fontSize: 'clamp(14px, 1.7vw, 20px)', margin: '0', fontFamily: 'Inter, sans-serif', color: '#4a7094ff' }}>
                {topText}
              </p>
            )}
            <h1 style={{ fontSize: 'clamp(36px, 7vw, 84px)', margin: '4px 0', fontFamily: 'Judson, serif', fontWeight: '500', lineHeight: '1', color: '#ffffff', textShadow: '2px 2px 26px rgba(0, 0, 0, 0.7)' }}>
              {midText}
            </h1>
            {botText && (
              <p style={{ fontSize: 'clamp(13px, 1.3vw, 18px)', margin: '0', fontFamily: 'Judson, serif', fontWeight: 'bold' }}>
                {botText}
              </p>
            )}
            {subBotText && (
              <p style={{ fontSize: 'clamp(13px, 1.3vw, 18px)', margin: '0', fontFamily: 'Judson, serif' }}>
                {subBotText}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* --- MOBILE GHOST TITLE (Centered horizontally outside desktop nesting) --- */}
      <div className="ghost-element mobile-title-card">
          {topText && (
            <p style={{ fontSize: '18px', margin: '0', fontFamily: 'Inter, sans-serif', color: '#4a7094ff' }}>
              {topText}
            </p>
          )}
          <h1 style={{ fontSize: 'clamp(46px, 9.2vw, 110.4px)', margin: '4px 0', fontFamily: 'Judson, serif', fontWeight: '500', lineHeight: '1', color: '#ffffff', textShadow: '2px 2px 26px rgba(0, 0, 0, 0.7)' }}>
            {midText}
          </h1>
          {botText && (
            <p style={{ fontSize: '16px', margin: '0', fontFamily: 'Judson, serif', fontWeight: 'bold' }}>
              {botText}
            </p>
          )}
          {subBotText && (
            <p style={{ fontSize: '16px', margin: '0', fontFamily: 'Judson, serif' }}>
              {subBotText}
            </p>
          )}
      </div>
    </nav>
    </>
  );
};

export default Header;